// Ported from video-editor repo (src/remotion/layout.ts) — 無改変コピー。
import {
  itemFreeze,
  itemSpeed,
  itemSpeedRamp,
  itemVolume,
  type EditProject,
  type SourceClip,
  type TimelineItem,
} from '../schema/project';
import { rampDisplayDurationSec, type SpeedRamp } from '../schema/speedRamp';
import { transitionOverlapSec } from '../schema/transition';

/**
 * タイムライン上の1アイテムを、コンポジション全体のフレーム座標に展開したもの。
 */
export interface LayoutItem {
  item: TimelineItem;
  clip: SourceClip;
  /** コンポジション全体における、このアイテムの開始フレーム */
  startFrame: number;
  /** このアイテムが画面に映っているフレーム数 */
  durationFrames: number;
  /** OffthreadVideo の startFrom に渡す値(コンポジション fps 換算) */
  sourceInFrame: number;
  /** OffthreadVideo の endAt に渡す値(コンポジション fps 換算) */
  sourceOutFrame: number;
  /** 再生速度(playbackRate)。1=等倍、>1=早送り、<1=スロー。speedRamp があるときは 1 固定。 */
  speed: number;
  /**
   * 速度ランプ (§4.1-L)。あるときは `speed` を無視し、フレームごとに素材フレームを差し替える
   * (ランプ中のシーン音声は鳴らせないので無音になる)。
   */
  speedRamp: SpeedRamp | undefined;
  /** 素材尺 (秒)。速度ランプの時間モデルで使う (outSec - inSec)。 */
  sourceDurationSec: number;
  /**
   * フリーズフレーム (§4.1-L)。無ければ null。
   * - `startFrame`: シーン内の表示フレームで、通常再生が止まる位置 (0 〜 baseFrames)
   * - `holdFrames`: 静止しているフレーム数
   * - `sourceFrame`: 静止させる素材フレーム (コンポジション fps 換算)
   */
  freeze: { startFrame: number; holdFrames: number; sourceFrame: number } | null;
  /** 埋め込み音声の実効音量(0〜1)。ミュート中は 0。静止画では意味を持たない。 */
  volume: number;
  /**
   * 直前アイテムとのトランジションのオーバーラップ量(フレーム)。無ければ 0。
   * 重なりの秒数は種類 (crossfade / wipe / slide / zoom / whiteout / flash) によらず
   * transition.durationSec (schema/transition.ts の transitionOverlapSec)。
   */
  overlapFrames: number;
}

/**
 * excluded=false のタイムラインアイテムを、トランジション (cut 以外) によるオーバーラップを
 * 考慮してコンポジション全体のフレーム座標に並べる。
 *
 * トランジションは「このアイテムの先頭に適用」される仕様のため、直前に採用済み
 * アイテムが存在する場合にのみ、その分だけ開始フレームを前倒しする
 * (= 直前アイテムの末尾と重ねて再生する)。
 */
export function computeTimelineLayout(project: EditProject): LayoutItem[] {
  const fps = project.output.fps;
  const clipsById = new Map(project.sourceClips.map((clip) => [clip.id, clip]));
  const included = project.timeline.filter((item) => !item.excluded);

  const layout: LayoutItem[] = [];
  let pointer = 0;

  included.forEach((item, index) => {
    const clip = clipsById.get(item.clipId);
    if (!clip) {
      throw new Error(
        `computeTimelineLayout: unknown clipId "${item.clipId}" for timeline item "${item.id}"`,
      );
    }

    const sourceInFrame = Math.round(item.inSec * fps);
    const sourceOutFrame = Math.round(item.outSec * fps);
    // 速度ランプ (§4.1-L) があるときは speed を無視する (排他。itemSpeed が 1 を返す)。
    const speedRamp = itemSpeedRamp(item);
    const speed = itemSpeed(item);
    const sourceDurationSec = Math.max(0, item.outSec - item.inSec);
    // 速度変更を反映した画面表示尺 (フリーズの静止ぶんを足す前)。
    // speed>1 なら短く(早送り)、<1 なら長く(スロー)になる。
    // **フリーズ / ランプが無いシーンでは従来と同じ式** (既存プロジェクトの総尺を変えない)。
    const baseFrames = Math.max(
      1,
      speedRamp
        ? Math.round(rampDisplayDurationSec((sourceOutFrame - sourceInFrame) / fps, speedRamp) * fps)
        : Math.round((sourceOutFrame - sourceInFrame) / speed),
    );
    // フリーズフレーム (§4.1-L)。静止のぶんだけ表示尺が伸びる。
    const freezeSetting = itemFreeze(item);
    const freeze = freezeSetting
      ? {
          startFrame: Math.max(
            0,
            Math.min(baseFrames, Math.round((Math.round(freezeSetting.atSourceSec * fps) - sourceInFrame) / speed)),
          ),
          holdFrames: Math.max(1, Math.round(freezeSetting.holdSec * fps)),
          sourceFrame: Math.round(freezeSetting.atSourceSec * fps),
        }
      : null;
    const durationFrames = baseFrames + (freeze ? freeze.holdFrames : 0);

    let overlapFrames = 0;
    if (index > 0) {
      overlapFrames = Math.max(
        0,
        Math.min(Math.round(transitionOverlapSec(item.transition) * fps), durationFrames - 1),
      );
    }

    const startFrame = Math.max(0, pointer - overlapFrames);

    layout.push({
      item,
      clip,
      startFrame,
      durationFrames,
      sourceInFrame,
      sourceOutFrame,
      speed,
      speedRamp,
      sourceDurationSec,
      freeze,
      volume: itemVolume(item),
      overlapFrames,
    });

    pointer = startFrame + durationFrames;
  });

  return layout;
}

/** コンポジション全体の尺(フレーム数)。durationInFrames は最低でも 1 を返す。 */
export function totalDurationFrames(project: EditProject): number {
  const layout = computeTimelineLayout(project);
  if (layout.length === 0) {
    return 1;
  }
  const last = layout[layout.length - 1]!;
  return Math.max(1, last.startFrame + last.durationFrames);
}
